import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";

const COPY = {
  post: {
    title: "Delete this post?",
    description:
      "This removes the post from the platform. Comments on it will also be removed. This cannot be undone.",
  },
  comment: {
    title: "Delete this comment?",
    description:
      "This removes the comment and its replies from the thread. This cannot be undone.",
  },
  reply: {
    title: "Delete this reply?",
    description: "This removes the reply from the thread. This cannot be undone.",
  },
};

/**
 * Platform-admin delete confirmation with optional author notification email.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {function} props.onOpenChange
 * @param {"post"|"comment"|"reply"|null} props.contentType
 * @param {boolean} props.busy
 * @param {string} [props.error]
 * @param {(options: {notify: boolean, moderatorComment: string}) => void} props.onConfirm
 * @returns {JSX.Element}
 */
export default function AdminModerationDeleteDialog({
  open,
  onOpenChange,
  contentType,
  busy,
  error,
  onConfirm,
}) {
  const copy = COPY[contentType] || COPY.post;
  const [sendEmail, setSendEmail] = useState(true);
  const [moderatorComment, setModeratorComment] = useState("");

  useEffect(() => {
    if (!open) return;
    setSendEmail(true);
    setModeratorComment("");
  }, [open, contentType]);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!busy) onOpenChange(nextOpen);
      }}
    >
      <DialogContent className="z-[70] sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription className="text-text-secondary">{copy.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 px-6 py-4">
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="admin-send-email" className="cursor-pointer">
              Send email
            </Label>
            <Switch
              id="admin-send-email"
              checked={sendEmail}
              onCheckedChange={setSendEmail}
              disabled={busy}
            />
          </div>
          <p className="text-sm text-text-muted">
            {sendEmail
              ? "The author will be notified and can reply to that email to appeal."
              : "The content will be removed without notifying the author."}
          </p>
          {sendEmail ? (
            <div className="space-y-2">
              <Label htmlFor="admin-moderator-comment">Moderator comment (optional)</Label>
              <Textarea
                id="admin-moderator-comment"
                value={moderatorComment}
                onChange={(event) => setModeratorComment(event.target.value)}
                placeholder="Included in the email if you write something here"
                disabled={busy}
                maxLength={2000}
              />
            </div>
          ) : null}
          {error ? <p className="text-sm text-error">{error}</p> : null}
        </div>

        <DialogFooter className="gap-2 sm:justify-end">
          <Button type="button" variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={busy}
            onClick={() =>
              onConfirm({
                notify: sendEmail,
                moderatorComment: sendEmail ? moderatorComment : "",
              })
            }
          >
            {busy ? "Deleting…" : sendEmail ? "Delete and send email" : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
